import React from 'react';
import { TableError } from '@Component/EmployeeTable/TableStates/TableError';
import { CreateSalaryRow } from '@Component/EmployeeTable/EmployeeTableRow/CreateSalaryRow.tsx';
import { TableLoading } from '@Component/EmployeeTable/TableStates/TableLoading';
import { EmployeeEmploymentRelationshipDTO } from '@Models/model.types';
import { EmployeeIdToEmploymentId, SelectAllState } from '@Page/SalaryRun/stateReducer';
import { APIError, UseFetchPaginatedState } from '@tlx/astro-shared';
import { TableEmptyCreateEmployee } from '@Component/EmployeeTable/TableStates/TableEmptyCreateEmployee';
import { TableEmptyResetFilter } from '@Component/EmployeeTable/TableStates/TableEmptyResetFilter';
import { FilterState, useIsAnyFilterActive } from '@Component/Filter/utils';
import { Column, Input, Spinner } from '@tlx/atlas';
import { useIndeterminateCheckbox } from '@Page/SalaryRun/hooks/useIndeterminateCheckbox.ts';
import { ColumnType } from '@Hooks/useOrdering.ts';

export const CreateSalaryTableBody = ({
    relationships,
    isRowChecked,
    onCheckRow,
    onChangeEmployment,
    filters,
}: {
    relationships: UseFetchPaginatedState<EmployeeEmploymentRelationshipDTO, APIError>;
    isRowChecked: (employeeToEmployment: EmployeeIdToEmploymentId) => boolean;
    onCheckRow: (employeeToEmployment: EmployeeIdToEmploymentId) => void;
    onChangeEmployment: (employeeToEmployment: EmployeeIdToEmploymentId) => void;
    filters: FilterState;
}) => {
    const filtersActive = useIsAnyFilterActive(filters);
    return (
        <>
            {!relationships.isLoading &&
                relationships.isEmpty &&
                (filtersActive ? <TableEmptyResetFilter {...filters} /> : <TableEmptyCreateEmployee />)}
            {relationships.error && <TableError />}
            {relationships.data.map((relationship) => {
                return (
                    <CreateSalaryRow
                        relationship={relationship}
                        key={relationship.employee.id}
                        checked={isRowChecked}
                        onCheck={onCheckRow}
                        onChangeEmployment={onChangeEmployment}
                    />
                );
            })}
            {relationships.isLoading && <TableLoading />}
        </>
    );
};

type ColumnProps = {
    columns: ColumnType[];
};

export const ColumnsRender = ({ columns }: ColumnProps) => {
    return (
        <>
            {columns.map((column) => (
                <Column
                    className="atl-whitespace-nowrap"
                    width={column.width}
                    key={column.id}
                    id={column.id}
                    sortable
                    data-testid={`column-${column.id}`}
                >
                    {column.label}
                </Column>
            ))}
        </>
    );
};

type SelectAllCheckboxProps = {
    selectAllState: SelectAllState;
    isCheckedAll: boolean;
    onCheckAll: () => void;
    someChecked: boolean;
};

export const SelectAllCheckbox: React.FC<SelectAllCheckboxProps> = ({
    selectAllState,
    isCheckedAll,
    onCheckAll,
    someChecked,
}) => {
    const indeterminateCheckboxRef = useIndeterminateCheckbox(someChecked);

    if (selectAllState === SelectAllState.SPINNER) {
        return (
            <div className="tsk-spinner">
                <Spinner size="small" />
            </div>
        );
    }
    return (
        <Input
            ref={indeterminateCheckboxRef}
            id={'check-all'}
            type={'checkbox'}
            data-testid="checkbox-all"
            checked={isCheckedAll}
            onChange={onCheckAll}
            disabled={selectAllState === SelectAllState.DISABLED}
        />
    );
};
